import { Brand, Context, Data, Effect, Equal, Layer, Array, Record, Tuple, pipe } from 'effect';
import { dual, identity } from 'effect/Function';
import * as ACS from '../../services/aspectConfigurationSpace';
import * as AspectAuth from '../../services/aspectAuthorization';
import * as Pr from '../product';
import { ProductId } from '../../api/productId';
import * as A from './article';
import * as Atch from './attachments';
import { GetAttachmentsError, reduceHandednessResults, } from './configurationSet/handednessResult';
import * as H from './handedness';
export { GetAttachmentsError };
export const ConfigurationSet = Brand.nominal();
const fromApiDataFirst = (configSpace, tenant) => {
    const structures = configSpace.structures;
    const findPossibleHandedness = H.possibleHandednessFromConfigSpace(configSpace);
    return pipe(configSpace.configurations, Array.map(configSet => {
        const structure = structures.find(s => s.id === configSet.structure);
        if (structure) {
            return ConfigurationSet({
                name: configSet.name,
                tenant,
                handedness: findPossibleHandedness(configSet.conditions),
                furnitureType: structure.furnitureType,
                parts: structure.parts.map(part => ({
                    name: part.name,
                    productType: part.productType,
                    isParent: part.isParent,
                    productIds: configSet.options[part.index].map(id => ProductId.make(id)),
                })),
            });
        }
        else
            return null;
    }), Array.filter((x) => x !== null));
};
export const fromApi = dual(2, fromApiDataFirst);
export const articles = (set) => pipe(set.parts, Array.flatMap(part => part.productIds.map(productId => A.fromProductId(productId, part.productType))));
export const possibleHandedness = (set) => pipe(set, articles, Array.map(A.attachments(set.tenant)), 
// NOTE: we might want to indicate that we've skipped over failed
// attempts at getting attachments in the return type eventually - for
// now, I think this makes sense in the context it is used in (in
// diziet)
Effect.allSuccesses, Effect.map(Atch.possibleHandedness));
const anyPartContainsProductId = dual(2, (set, productId) => set.parts.some(part => part.productIds.includes(productId)));
export const containsArticle = (set, article) => articles(set).some(Equal.equals(article));
export const getForTenant = (tenant) => Effect.andThen(ACS.AspectConfigurationSpaceService, s => s.getSpaces(tenant)).pipe(Effect.map(Array.flatMap(fromApi(tenant))));
export const getByName = dual(2, (tenant, name) => getForTenant(tenant).pipe(Effect.map(Array.filter(set => set.name === name))));
/**
 * Find Product IDs which are in Configuration Sets that the given Product is in.
 * Grouped by Configuration Set 'Part name'.
 */
export const relatedArticles = dual(2, (tenant, product) => Effect.gen(function* (_) {
    const spaces = yield* _(getForTenant(tenant));
    return pipe(spaces, Array.filter(anyPartContainsProductId(Pr.id(product))), Array.map(set => Data.tuple(set, set.parts)), Array.map(([set, parts]) => Data.tuple(set, pipe(parts, Array.flatMap(part => part.productIds.map(productId => A.fromProductId(productId, part.productType)))))), Array.groupBy(([set, _]) => set.name), Record.map(Array.flatMap(([_, articles]) => articles)));
}));
export class FixtuurAspectConfigurationSetService extends Context.Tag('@fixtuur/backpack/FixtuurAspectConfigurationSetService')() {
}
export const LiveFixtuurAspectConfigurationSetService = Layer.effect(FixtuurAspectConfigurationSetService, Effect.gen(function* (_) {
    const configSpaceService = yield* _(ACS.AspectConfigurationSpaceService);
    const articleService = yield* _(A.FixtuurAspectArticleService);
    const possibleHandedness = set => pipe(set, articles, Array.map(article => articleService
        .attachments(set.tenant, article)
        .pipe(Effect.catchAll(originalError => new GetAttachmentsError({ article, originalError })))), Effect.partition(identity), Effect.map(Tuple.mapSecond(Atch.possibleHandedness)));
    const allForTenant = tenant => configSpaceService.getSpaces(tenant).pipe(Effect.map(Array.flatMap(fromApi(tenant))));
    const allContainingArticle = (article, tenant) => allForTenant(tenant).pipe(Effect.map(Array.filter(set => containsArticle(set, article))));
    const possibleHandednessForArticle = (article, tenant) => allContainingArticle(article, tenant).pipe(Effect.map(Array.map(possibleHandedness)), Effect.flatMap(x => Effect.all(x)), Effect.map(reduceHandednessResults));
    return {
        allContainingArticle,
        allForTenant,
        possibleHandedness,
        possibleHandednessForArticle,
    };
})).pipe(Layer.provide(ACS.layer), Layer.provide(AspectAuth.layer), Layer.provide(A.ToastFixtuurAspectArticleService));
